import L, { DivIcon, MarkerCluster } from 'leaflet';
import { CSSProperties } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { L3ServiceLocationViewModel, MaintenanceStatus } from '../../model';
import { L3ServiceMarkerContainer } from '../L3ServiceMarkerContainer/L3ServiceMarkerContainer';
import { L3ServiceStatus, serviceStatusColorMap } from '../../../l3-services/model';

type LocationAwareLeafletIconOptions = L.DivIconOptions & { location: L3ServiceLocationViewModel };

export const createL3ServiceMarkerClusterIcon = (cluster: MarkerCluster): DivIcon => {
  const markers = cluster.getAllChildMarkers();
  const markerLocations: L3ServiceLocationViewModel[] = markers
    .map(marker => (marker.getIcon().options as LocationAwareLeafletIconOptions).location);
  const colors: { [P in L3ServiceStatus]: number } = {
    [L3ServiceStatus.Pending]: 0,
    [L3ServiceStatus.Archived]: 0,
    [L3ServiceStatus.Ready]: 0,
    [L3ServiceStatus.Unavailable]: 0,
    [L3ServiceStatus.Available]: 0,
  };
  const markersColorsCount = markers.reduce(
    (result, marker) => {
      const newResult = {
        ...result,
      };
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const iconOptions = marker.options.icon!.options as LocationAwareLeafletIconOptions;
      newResult[iconOptions.location.serviceStatus] += 1;
      return newResult;
    },
    colors,
  );
  const background = mapMarkerColorsToGradient(markersColorsCount);
  const displayNotificationsIcon = markerLocations.some(location => Boolean(location.notificationCount));
  const isAnyPlannedMaintenanceWorkInLocations = markerLocations.some(location => location.maintenanceStatuses?.includes(MaintenanceStatus.Planned));
  const isAnyActiveMaintenanceWorkInLocations = markerLocations.some(location => location.maintenanceStatuses?.includes(MaintenanceStatus.Active));
  const displayPlannedMaintenanceWorksIcon = isAnyPlannedMaintenanceWorkInLocations && !isAnyActiveMaintenanceWorkInLocations;

  return L.divIcon({
    html: renderToStaticMarkup(<L3ServiceMarkerContainer
      displayNotificationsIcon={displayNotificationsIcon}
      displayActiveMaintenanceWorksIcon={isAnyActiveMaintenanceWorkInLocations}
      displayPlannedMaintenanceWorksIcon={displayPlannedMaintenanceWorksIcon}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...background,
        }}
      >
        <span
          style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
          }}
        >{cluster.getChildCount()}</span>
      </div>
    </L3ServiceMarkerContainer>),
    className: 'custom-marker-cluster',
    iconSize: L.point(40, 40, true),
  });
};

export const mapMarkerColorsToGradient = (colors: { [P in L3ServiceStatus]: number }): CSSProperties => {
  const isOnlyOneColor = Object.values(colors).filter(Boolean).length === 1;
  if (isOnlyOneColor) {
    const serviceStatus = (Object.keys(colors) as L3ServiceStatus[]).find(status => Boolean(colors[status as keyof typeof colors]));
    if (serviceStatus) {
      return {
        background: serviceStatusColorMap[serviceStatus],
      };
    } else {
      throw new Error('Invalid service status provided.');
    }
  } else {
    const totalElements = Object.values(colors).reduce((total, current) => total + current, 0);
    const sortedColorsCollection = Object
      .keys(colors)
      .map((status) => ({
        color: serviceStatusColorMap[status as L3ServiceStatus],
        count: colors[status as keyof typeof colors],
      }))
      .filter(colorElement => colorElement.count)
      .sort((prev, next) => next.count - prev.count)
      .map((colorElement) => {
        return { color: colorElement.color, percent: Math.round((colorElement.count / totalElements) * 100) };
      })
      .reduce<{ color: string; percentRange: [number, number] }[]>((accumulator, element, index, array) => {
        const previousElementEndOfPercentRange = (index ? accumulator[index - 1] : {
          color: '',
          percentRange: [0, 0],
        }).percentRange[1];
        const currentElementEndOfPercentRange = index + 1 === array.length
          ? 100
          : previousElementEndOfPercentRange + element.percent;
        return [
          ...accumulator,
          {
            color: element.color,
            percentRange: [index && previousElementEndOfPercentRange, currentElementEndOfPercentRange],
          },
        ];
      }, []);

    const colorsGradient = sortedColorsCollection
      .reduce((content, element) => `${content}${element.color} ${element.percentRange[0]}% ${element.percentRange[1]}%, `, '');

    return {
      backgroundImage: `conic-gradient(${colorsGradient.slice(0, -2)})`,
    };
  }
};